import type { ReactNode } from 'react';

import styles from './tabs.module.scss';

export type TabItem<TValue extends string = string> = {
  value: TValue;
  label: ReactNode;
  icon?: ReactNode;
  badge?: number;
  disabled?: boolean;
};

export type TabsProps<TValue extends string = string> = {
  ariaLabel: string;
  items: TabItem<TValue>[];
  value: TValue;
  onChange: (value: TValue) => void;
  className?: string;
  stackOnTablet?: boolean;
  stretch?: boolean;
  disabled?: boolean;
};

export function Tabs<TValue extends string = string>({
  ariaLabel,
  items,
  value,
  onChange,
  className,
  stackOnTablet = false,
  stretch = false,
  disabled = false
}: TabsProps<TValue>) {
  const mergedClassName = [
    styles.tabs,
    stackOnTablet ? styles.stackOnTablet : undefined,
    stretch ? styles.stretch : undefined,
    className
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <nav className={mergedClassName} aria-label={ariaLabel} role="tablist">
      {items.map((item) => {
        const isActive = item.value === value;
        const isDisabled = disabled || Boolean(item.disabled);

        return (
          <button
            key={item.value}
            type="button"
            className={[styles.tab, isActive ? styles.active : undefined]
              .filter(Boolean)
              .join(' ')}
            onClick={() => onChange(item.value)}
            aria-selected={isActive}
            role="tab"
            disabled={isDisabled}
          >
            {item.icon ? (
              <span className={styles.icon} aria-hidden="true">
                {item.icon}
              </span>
            ) : null}
            {item.label}
            {item.badge != null && item.badge > 0 ? (
              <span className={styles.badge} aria-label={`${item.badge} new`}>
                {item.badge > 99 ? '99+' : item.badge}
              </span>
            ) : null}
          </button>
        );
      })}
    </nav>
  );
}
